import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { AdminResourceCheckResponse } from '~~/layers/crane-manager/server/api/crane-manager/resource-check/[resource].get'
import { mergeCapacitorDesignerFetchOptions } from '~~/layers/base/utils/mergeCapacitorDesignerFetchOptions'

/** Same shape as `mergeCapacitorDesignerFetchOptions` input (same-origin; no designerBaseUrl). */
type ResourceCheckFetchInput = Record<string, unknown> & {
    baseURL?: string
    headers?: HeadersInit
}

/**
 * Session cache of successful `GET /api/crane-manager/resource-check/:key` results for the generic admin
 * route middleware. Failed checks are not cached (403/404/network) so denials and transient errors
 * stay correct; logout clears the map via `clearCheckCache` so the next user does not inherit
 * allow-list entries.
 *
 * LIVES OUTSIDE `layers/base/stores/` (see `adminResourcesMeta.store.ts`) so this composable is
 * not in Nuxt `imports.dirs` global auto-imports.
 */
export const useAdminResourceCheckStore = defineStore('adminResourceCheck', () => {
    /** Only `200` responses: keys the session may access for generic `/crane-manager/:resource` routes. */
    const allowedByKey = ref<Record<string, AdminResourceCheckResponse>>({})

    function clearCheckCache(): void {
        allowedByKey.value = {}
    }

    /**
     * Resolves an allowed resource, using `allowedByKey` when this key already returned `200` in
     * the current session. Otherwise performs `$fetch` and stores the successful payload.
     *
     * @throws Rejects the same way `$fetch` would on non-2xx (middleware treats as “deny/redirect”)
     */
    async function ensureResourceAllowed(
        resource: string,
        baseFetchOptions: ResourceCheckFetchInput
    ): Promise<AdminResourceCheckResponse> {
        const key = resource.trim()
        if (!key) {
            return Promise.reject(new Error('Missing admin resource key'))
        }

        const hit = allowedByKey.value[key]
        if (hit) {
            return hit
        }

        const fetchOptions = await mergeCapacitorDesignerFetchOptions(baseFetchOptions)
        const res = await $fetch<AdminResourceCheckResponse>(
            `/api/crane-manager/resource-check/${encodeURIComponent(key)}`,
            fetchOptions
        )
        allowedByKey.value = { ...allowedByKey.value, [key]: res }
        return res
    }

    return {
        allowedByKey,
        ensureResourceAllowed,
        clearCheckCache,
    }
})
